/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';

import { ExpandCaret } from '@/Client/Icons';
import { COLORS, FONT } from '@/Client/Partials/CloseoutReviewFlow/closeoutTokens';

import { pxToRem } from '@/Client/Styles/pxToRem';
import { useProgressAlt } from './progressAltContext';
import { COLLAPSED_PERIOD_CHIP_MAX_WIDTH_PX, CONTROL_HEIGHT_PX } from './progressAltMetrics';
import { ProgressAltSegmentedControl } from './ProgressAltSegmentedControl';
import { FOCUS_RING_STYLE } from './progressAltStyles';

/**
 * Inked like the segmented control's chosen option, because that is what it is: the same selection,
 * with the alternatives put away rather than dropped. The caret makes it the collapsed column's only
 * control, so nothing else has to earn a place in the width the collapse just gave up.
 */
const CHIP_STYLE = css`
  label: PROGRESS_ALT_PERIOD_CHIP_STYLE;
  box-sizing: border-box;
  flex: 1;
  height: ${CONTROL_HEIGHT_PX}px;
  min-width: 0;
  max-width: ${COLLAPSED_PERIOD_CHIP_MAX_WIDTH_PX}px;
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
  padding: 0 4px;
  border: 1px solid ${COLORS.ink};
  border-radius: 0;
  background: ${COLORS.ink};
  color: ${COLORS.white};
  font-family: ${FONT.mono};
  font-size: ${pxToRem(10)};
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  cursor: pointer;

  &:hover {
    border-color: ${COLORS.midtone};
  }

  ${FOCUS_RING_STYLE}
`;

/** Ellipsis over wrapping, so a long unit narrows the label rather than growing the column back. */
const CHIP_LABEL_STYLE = css`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/**
 * Chooses how wide a time bucket a Progress Alt row covers.
 *
 * Lives in the date column's header cell, above the labels it decides the wording of, and wears
 * whichever shape that cell has room for. Collapsed there is only room to name the period in force,
 * so choosing another is the same gesture as reading one of the dates: open the column back up.
 */
export const ProgressAltPeriodPicker = () => {
  const {
    periodOptions,
    periodId,
    selectPeriod,
    dateColumnCollapsed,
    toggleDateColumnCollapsed,
  } = useProgressAlt();

  if (!dateColumnCollapsed) {
    return (
      <ProgressAltSegmentedControl
        ariaLabel='Group photos by'
        segments={periodOptions.map(({ id, label, shortLabel }) => ({ id, label, content: shortLabel }))}
        selectedId={periodId}
        onSelect={selectPeriod}
      />
    );
  }

  const selected = periodOptions.find(option => option.id === periodId);
  return (
    <button
      type='button'
      css={CHIP_STYLE}
      data-testid='progress-alt-period-trigger'
      aria-label={`Grouped by ${selected?.label ?? periodId} — expand date column to change`}
      aria-expanded={false}
      // A click on the collapsed chip opens the column back up: the options live in the open one.
      onClick={toggleDateColumnCollapsed}
    >
      <span css={CHIP_LABEL_STYLE}>{selected?.initialLabel ?? periodId}</span>
      {/* Points right, at the width the column is about to take back. */}
      <ExpandCaret color={COLORS.white} />
    </button>
  );
};
